import { cn } from '@/lib/utils'

/** Storefront shimmer block — uses luxury-themed skeleton-shimmer from global CSS. */
export function StoreSkeleton({ className }: { className?: string }) {
  return <div className={cn('skeleton-shimmer', className)} aria-hidden />
}

export function SectionHeadingSkeleton({ withLink = true }: { withLink?: boolean }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
      <div className="space-y-3">
        <StoreSkeleton className="h-9 w-48 rounded-lg" />
        <StoreSkeleton className="h-4 w-64 max-w-full rounded-md" />
      </div>
      {withLink && <StoreSkeleton className="h-4 w-24 rounded-md hidden sm:block" />}
    </div>
  )
}

/** Matches {@link ProductCard} layout. */
export function ProductCardSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        'glass-card gold-border-glow overflow-hidden flex h-full flex-col',
        className
      )}
      aria-hidden
    >
      <div className="relative aspect-[4/5] w-full overflow-hidden border-b border-luxury-700/50 bg-luxury-950/60">
        <StoreSkeleton className="absolute inset-0 rounded-none opacity-70" />
      </div>
      <div className="p-4 space-y-3 bg-luxury-900/20">
        <StoreSkeleton className="h-2.5 w-16 rounded" />
        <StoreSkeleton className="h-5 w-3/4 rounded-md" />
        <StoreSkeleton className="h-3 w-1/2 rounded-md" />
        <div className="flex items-center gap-2 pt-1">
          <StoreSkeleton className="h-5 w-20 rounded-md" />
          <StoreSkeleton className="h-3 w-14 rounded-md" />
        </div>
      </div>
    </div>
  )
}

export function ProductGridSkeleton({
  count = 8,
  className,
}: {
  count?: number
  className?: string
}) {
  return (
    <div
      className={cn(
        'grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6',
        className
      )}
      aria-busy
      aria-label="Loading products"
    >
      {Array.from({ length: count }).map((_, i) => (
        <ProductCardSkeleton key={i} />
      ))}
    </div>
  )
}

export function ShopPageSkeleton() {
  return (
    <div aria-busy aria-label="Loading shop">
      <SectionHeadingSkeleton withLink={false} />
      <div className="flex flex-col lg:flex-row gap-8">
        <aside className="lg:w-56 shrink-0">
          <div className="glass-card p-4 space-y-4">
            <StoreSkeleton className="h-4 w-20 rounded-md" />
            <div className="space-y-2">
              <StoreSkeleton className="h-3 w-16 rounded" />
              <StoreSkeleton className="h-10 w-full rounded-xl" />
            </div>
            <div className="space-y-2">
              <StoreSkeleton className="h-3 w-10 rounded" />
              <StoreSkeleton className="h-10 w-full rounded-xl" />
            </div>
          </div>
        </aside>
        <div className="flex-1">
          <ProductGridSkeleton count={8} />
        </div>
      </div>
    </div>
  )
}

export function SearchPageSkeleton() {
  return (
    <div className="store-container py-10" aria-busy aria-label="Loading search">
      <SectionHeadingSkeleton withLink={false} />
      <StoreSkeleton className="h-11 w-full max-w-xl rounded-xl mb-8" />
      <ProductGridSkeleton count={6} className="lg:grid-cols-3" />
    </div>
  )
}

export function WishlistPageSkeleton() {
  return (
    <div aria-busy aria-label="Loading wishlist">
      <ProductGridSkeleton count={4} />
    </div>
  )
}

/** Matches collapsed order cards on My Orders. */
export function OrderCardSkeleton({ expanded = false }: { expanded?: boolean }) {
  return (
    <div className="glass-card gold-border-glow p-6 space-y-4" aria-hidden>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="space-y-2">
          <StoreSkeleton className="h-5 w-36 rounded-md" />
          <StoreSkeleton className="h-4 w-48 rounded-md" />
        </div>
        <StoreSkeleton className="h-7 w-24 rounded-full" />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <StoreSkeleton className="h-7 w-28 rounded-md" />
        <div className="flex gap-2">
          <StoreSkeleton className="h-9 w-24 rounded-xl" />
          <StoreSkeleton className="h-9 w-28 rounded-xl" />
        </div>
      </div>
      {expanded && (
        <div className="pt-4 border-t border-luxury-800 space-y-4">
          <StoreSkeleton className="h-4 w-full max-w-md rounded-md" />
          <div className="rounded-xl border border-luxury-800 bg-luxury-950/50 p-4 space-y-3">
            <StoreSkeleton className="h-4 w-16 rounded-md" />
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="flex justify-between gap-4">
                <StoreSkeleton className="h-4 flex-1 max-w-xs rounded-md" />
                <StoreSkeleton className="h-4 w-16 rounded-md" />
              </div>
            ))}
            <StoreSkeleton className="h-5 w-32 rounded-md ml-auto" />
          </div>
        </div>
      )}
    </div>
  )
}

export function OrdersListSkeleton({ count = 5 }: { count?: number }) {
  return (
    <div className="space-y-4" aria-busy aria-label="Loading orders">
      <StoreSkeleton className="h-4 w-28 rounded-md" />
      {Array.from({ length: count }).map((_, i) => (
        <OrderCardSkeleton key={i} expanded={i === 0} />
      ))}
      <div className="flex items-center justify-center gap-3 pt-2">
        <StoreSkeleton className="h-9 w-9 rounded-lg" />
        <StoreSkeleton className="h-4 w-20 rounded-md" />
        <StoreSkeleton className="h-9 w-9 rounded-lg" />
      </div>
    </div>
  )
}

export function ProductDetailPageSkeleton() {
  return (
    <div className="store-container pt-5 pb-16" aria-busy aria-label="Loading product">
      <div className="grid lg:grid-cols-2 gap-10">
        <div className="space-y-4">
          <StoreSkeleton className="w-full min-h-80 lg:min-h-[28rem] rounded-2xl" />
          <div className="flex gap-2">
            {Array.from({ length: 4 }).map((_, i) => (
              <StoreSkeleton key={i} className="h-16 w-16 shrink-0 rounded-lg" />
            ))}
          </div>
        </div>
        <div className="space-y-5">
          <StoreSkeleton className="h-3 w-20 rounded" />
          <StoreSkeleton className="h-10 w-4/5 max-w-md rounded-lg" />
          <StoreSkeleton className="h-4 w-2/3 max-w-sm rounded-md" />
          <div className="space-y-2 pt-2">
            <StoreSkeleton className="h-3 w-full rounded-md" />
            <StoreSkeleton className="h-3 w-full rounded-md" />
            <StoreSkeleton className="h-3 w-5/6 rounded-md" />
          </div>
          <div className="glass-card p-4 space-y-2">
            <StoreSkeleton className="h-4 w-32 rounded-md" />
            <StoreSkeleton className="h-3 w-full rounded-md" />
          </div>
          <div className="glass-card p-5 space-y-4 mt-4">
            <div className="flex items-center justify-between">
              <StoreSkeleton className="h-8 w-28 rounded-md" />
              <StoreSkeleton className="h-10 w-28 rounded-lg" />
            </div>
            <StoreSkeleton className="h-4 w-24 rounded-md" />
            <div className="flex gap-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <StoreSkeleton key={i} className="h-9 flex-1 rounded-lg" />
              ))}
            </div>
            <StoreSkeleton className="h-11 w-full rounded-xl" />
            <StoreSkeleton className="h-11 w-full rounded-xl" />
          </div>
        </div>
      </div>
    </div>
  )
}

export function StoreLegalPageSkeleton() {
  return (
    <div className="space-y-6" aria-busy aria-label="Loading policy">
      {Array.from({ length: 6 }).map((_, i) => (
        <StoreSkeleton
          key={i}
          className={cn(
            'h-3 rounded-md',
            i % 3 === 2 ? 'w-4/5' : 'w-full'
          )}
        />
      ))}
      <StoreSkeleton className="h-3 w-2/3 rounded-md" />
      <div className="pt-4 space-y-3">
        <StoreSkeleton className="h-4 w-40 rounded-md" />
        {Array.from({ length: 4 }).map((_, i) => (
          <StoreSkeleton key={i} className="h-3 w-full rounded-md" />
        ))}
      </div>
    </div>
  )
}

export function StoreOrderDetailPageSkeleton() {
  return (
    <div className="store-container py-10" aria-busy aria-label="Loading order">
      <StoreSkeleton className="h-4 w-32 rounded-md mb-6" />
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div className="space-y-2">
          <StoreSkeleton className="h-9 w-44 rounded-lg" />
          <StoreSkeleton className="h-4 w-36 rounded-md" />
        </div>
        <StoreSkeleton className="h-7 w-24 rounded-full" />
      </div>
      <div className="glass-card gold-border-glow p-6 mb-6 space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <StoreSkeleton key={i} className="h-4 w-full max-w-lg rounded-md" />
        ))}
      </div>
      <div className="glass-card gold-border-glow p-6 space-y-4">
        <StoreSkeleton className="h-6 w-36 rounded-md" />
        <div className="rounded-xl border border-luxury-800 bg-luxury-950/50 p-4 space-y-3">
          <StoreSkeleton className="h-4 w-16 rounded-md" />
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex justify-between gap-4">
              <StoreSkeleton className="h-4 flex-1 rounded-md" />
              <StoreSkeleton className="h-4 w-16 rounded-md" />
            </div>
          ))}
          <StoreSkeleton className="h-5 w-32 rounded-md ml-auto" />
        </div>
      </div>
    </div>
  )
}

export function HomeHeroSkeleton() {
  return (
    <section className="relative overflow-hidden arabesque-pattern" aria-hidden>
      <div className="store-container relative py-16 md:py-24 grid lg:grid-cols-2 gap-12 items-center">
        <div className="space-y-5">
          <StoreSkeleton className="h-3 w-56 max-w-full rounded" />
          <StoreSkeleton className="h-12 md:h-16 w-full max-w-xl rounded-xl" />
          <StoreSkeleton className="h-5 w-full max-w-lg rounded-md" />
          <StoreSkeleton className="h-5 w-4/5 max-w-md rounded-md" />
          <div className="flex flex-wrap gap-4 pt-2">
            <StoreSkeleton className="h-11 w-40 rounded-xl" />
            <StoreSkeleton className="h-11 w-36 rounded-xl" />
          </div>
        </div>
        <div className="w-full">
          <StoreSkeleton className="w-full aspect-[21/9] rounded-2xl" />
        </div>
      </div>
    </section>
  )
}

export function HomeCategoriesSkeleton() {
  return (
    <section className="store-container py-16" aria-hidden>
      <SectionHeadingSkeleton />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {Array.from({ length: 8 }).map((_, i) => (
          <div
            key={i}
            className="glass-card gold-border-glow p-6 flex items-center justify-center min-h-[5.5rem]"
          >
            <StoreSkeleton className="h-6 w-20 rounded-md" />
          </div>
        ))}
      </div>
    </section>
  )
}

export function HomeStorySkeleton() {
  return (
    <section className="store-container py-16" aria-hidden>
      <div className="glass-card gold-border-glow p-8 md:p-12 grid md:grid-cols-2 gap-8 items-center">
        <div className="space-y-4">
          <StoreSkeleton className="h-8 w-40 rounded-lg" />
          <StoreSkeleton className="h-4 w-full rounded-md" />
          <StoreSkeleton className="h-4 w-full rounded-md" />
          <StoreSkeleton className="h-4 w-5/6 rounded-md" />
        </div>
        <StoreSkeleton className="h-32 md:h-36 w-full rounded-2xl" />
      </div>
    </section>
  )
}

/** Checkout contact / delivery details while persisted form hydrates. */
export function CheckoutDetailsSkeleton() {
  return (
    <div className="space-y-6" aria-busy aria-label="Loading your details">
      <div className="glass-card gold-border-glow p-6 space-y-4">
        <StoreSkeleton className="h-6 w-40 rounded-md" />
        <StoreSkeleton className="h-10 w-full rounded-xl" />
        <StoreSkeleton className="h-10 w-full rounded-xl" />
        <StoreSkeleton className="h-10 w-full rounded-xl" />
      </div>
      <div className="glass-card gold-border-glow p-6 space-y-4">
        <StoreSkeleton className="h-6 w-44 rounded-md" />
        <StoreSkeleton className="h-10 w-full rounded-xl" />
        <StoreSkeleton className="h-10 w-full rounded-xl" />
        <StoreSkeleton className="h-10 w-full rounded-xl" />
        <StoreSkeleton className="h-10 w-2/3 rounded-xl" />
      </div>
    </div>
  )
}

/** Full home page placeholder — hero, categories, products, story. */
export function HomePageSkeleton() {
  return (
    <div aria-busy aria-label="Loading home page">
      <HomeHeroSkeleton />
      <HomeCategoriesSkeleton />
      <HomeProductSectionSkeleton />
      <HomeStorySkeleton />
    </div>
  )
}

export function HomeProductSectionSkeleton({ cols = 4 }: { cols?: number }) {
  return (
    <section className="store-container py-8" aria-hidden>
      <SectionHeadingSkeleton />
      <ProductGridSkeleton count={cols} />
    </section>
  )
}
